import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/hooks/useSupabase';
import { showToast } from '@/components/Toast';
import { isValidProposalToken } from '@/mocks/template-config';

export default function LoginCard() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [proposalCode, setProposalCode] = useState('');
  const [showProposalInput, setShowProposalInput] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [consultLoading, setConsultLoading] = useState(false);

  const handleLogin = async () => {
    if (!email.trim()) {
      showToast('Informe o e-mail.', 'warning');
      return;
    }
    if (!password.trim()) {
      showToast('Informe a senha.', 'warning');
      return;
    }
    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      });
      if (error) {
        if (error.message.includes('Invalid login')) {
          showToast('E-mail ou senha inválidos.', 'error');
        } else if (error.message.includes('Email not confirmed')) {
          showToast('E-mail não confirmado. Verifique sua caixa de entrada.', 'warning');
        } else {
          showToast('Erro ao fazer login: ' + error.message, 'error');
        }
        return;
      }
      showToast('Login realizado com sucesso!', 'success');
      navigate('/admin/dashboard');
    } catch {
      showToast('Não foi possível conectar ao servidor. Tente novamente.', 'error');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleAdminAccess = () => {
    navigate('/admin/login');
  };

  const handleConsultProposal = () => {
    const code = proposalCode.trim().toLowerCase();
    if (!code) {
      showToast('Digite o código da proposta.', 'warning');
      return;
    }
    if (!isValidProposalToken(code)) {
      showToast('Código de proposta inválido. Deve conter 16 caracteres hexadecimais.', 'warning');
      return;
    }
    setConsultLoading(true);
    setTimeout(() => {
      setConsultLoading(false);
      navigate(`/proposta/${code}`);
    }, 300);
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      showToast('Informe seu e-mail acima para recuperar a senha.', 'warning');
      return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim());
      if (error) {
        showToast('Erro ao enviar e-mail de recuperação: ' + error.message, 'error');
        return;
      }
      showToast('E-mail de recuperação enviado! Verifique sua caixa de entrada.', 'success');
    } catch {
      showToast('Não foi possível conectar ao servidor. Tente novamente.', 'error');
    }
  };

  return (
    <div className="relative z-10 flex flex-col items-center px-4 pb-8">
      <div className="w-full max-w-md bg-brand-card/90 backdrop-blur-sm border border-brand-gold/30 rounded-lg p-6 md:p-8">
        {/* Portal Header */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full border border-brand-gold flex items-center justify-center">
            <i className="ri-user-3-line text-brand-gold text-lg" />
          </div>
          <h2 className="text-brand-gold text-lg md:text-xl font-semibold tracking-wide">
            PORTAL DO CLIENTE
          </h2>
        </div>

        <p className="text-brand-text-muted text-center text-sm mb-6">
          Acesse suas propostas, acompanhe seus projetos e mantenha-se informado.
        </p>

        {/* Login Form */}
        <div className="space-y-4">
          <div className="relative">
            <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-muted" />
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-mail ou CPF/CNPJ"
              className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-3 pl-10 pr-4 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50 transition-colors"
            />
          </div>

          <div className="relative">
            <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-muted" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha"
              className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-3 pl-10 pr-10 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50 transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text-muted hover:text-brand-text transition-colors"
            >
              <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
            </button>
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-brand-gold/30 bg-brand-dark/50 text-brand-gold focus:ring-brand-gold/50"
              />
              <span className="text-brand-text-muted">Lembrar-me</span>
            </label>
            <button
              onClick={handleForgotPassword}
              className="text-brand-gold hover:text-brand-gold-light transition-colors"
            >
              Esqueci minha senha
            </button>
          </div>

          <button
            onClick={handleLogin}
            disabled={loginLoading}
            className="w-full bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-semibold py-3 rounded-md flex items-center justify-center gap-2 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            {loginLoading ? (
              <div className="w-5 h-5 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" />
            ) : (
              <i className="ri-lock-unlock-line" />
            )}
            {loginLoading ? 'Entrando...' : 'ENTRAR'}
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-brand-gold/20" />
          <span className="text-brand-text-muted text-xs">ou</span>
          <div className="flex-1 h-px bg-brand-gold/20" />
        </div>

        {/* Admin Button */}
        <button
          onClick={handleAdminAccess}
          className="w-full border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 py-3 rounded-md flex items-center justify-center gap-2 transition-colors text-sm whitespace-nowrap cursor-pointer"
        >
          <i className="ri-dashboard-line" />
          ACESSO DO ADMINISTRADOR
        </button>
      </div>

      {/* Proposal Code Lookup */}
      <div className="mt-4 w-full max-w-md">
        {!showProposalInput ? (
          <button
            onClick={() => setShowProposalInput(true)}
            className="w-full text-brand-text-muted hover:text-brand-gold text-sm flex items-center justify-center gap-2 transition-colors py-2"
          >
            <i className="ri-file-search-line" />
            Consultar proposta por código
          </button>
        ) : (
          <div className="bg-brand-card/80 backdrop-blur-sm border border-brand-gold/20 rounded-lg p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={proposalCode}
                onChange={(e) => setProposalCode(e.target.value)}
                placeholder="Digite o código da proposta"
                className="flex-1 bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50"
              />
              <button
                onClick={handleConsultProposal}
                disabled={consultLoading}
                className="bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium px-4 py-2 rounded-md text-sm transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {consultLoading ? (
                  <div className="w-4 h-4 border-2 border-brand-dark/30 border-t-brand-dark rounded-full animate-spin" />
                ) : (
                  'Consultar'
                )}
              </button>
            </div>
            <button
              onClick={() => setShowProposalInput(false)}
              className="text-brand-text-muted hover:text-brand-gold text-xs mt-2 transition-colors"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}